import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Tooltip,
  Button,
  Menu
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import axios from 'axios';
import useSWR from 'swr';
import { fleetApi } from '../../../../api/axiosClient';
import PickerField from '../../../../components/PickerField';
import usePickerPagination from '../../../../hooks/usePickerPagination';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useMasterVehicleList } from '../../../../stores/masterVehicleStore';
import { useCompanyList, useCompanyDataList } from '../../../../stores/companyStore';
import { useDriverList } from '../../../../stores/driverStore';
import { useDriverLicenseList } from '../../../../stores/driverLicenseStore';
import { useServiceTypeList } from '../../../../stores/serviceTypeStore';
import { useVehicleLicenseList } from '../../../../stores/VehicleLicenseStore';
import { fetchAlertNotificationDocsSearch, fetchHistoryGpsNotifierSearch } from '../../../../stores/alertNotificationStore';
import { useLanguage } from '../../../../utils/i18n';
import { API_BASES } from '../../../../config/constants';
import { timezone } from '../../../../utils/timeZone';

const getTzParam = () => {
  const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
  return `?tz=${tz}`;
};

const useUserCompanyAccessHook = (page, pageSize, searchKey, searchValue) => {
  const userId =
    typeof window !== 'undefined'
      ? (JSON.parse(localStorage.getItem('user'))?.id ??
        localStorage.getItem('user_id') ??
        JSON.parse(localStorage.getItem('user'))?.user_id)
      : null;
  const limit = pageSize;
  const offset = page * pageSize;
  // search server side based on user description
  const searchQ = searchValue ? `&${searchKey || 'search'}=${encodeURIComponent(searchValue)}` : '';
  const url = userId ? `/assign-company-access/list?userId=${encodeURIComponent(userId)}&limit=${limit}&offset=${offset}${searchQ}` : null;

  const { data, error } = useSWR(url, async (u) => {
    const resp = await fleetApi.get(u + getTzParam());
    const payload = resp?.data ?? resp;
    let rows = Array.isArray(payload) ? payload : (payload?.data ?? payload?.result ?? payload?.rows ?? []);
    rows = rows.filter((r) => r.selected === true);
    return { rows, total: payload?.totalRecord ?? rows.length };
  });

  return {
    data: data?.rows ?? [],
    totalRecords: data?.total ?? 0,
    isLoading: !error && !data,
    isError: error
  };
};

const useUserVehicleAccessHook = (page, pageSize, searchKey, searchValue, companyId) => {
  const userId =
    typeof window !== 'undefined'
      ? (localStorage.getItem('user_id') ??
        JSON.parse(localStorage.getItem('user'))?.id ??
        JSON.parse(localStorage.getItem('user'))?.user_id)
      : null;
  const limit = pageSize;
  const offset = page * pageSize;
  const searchQ = searchValue ? `&${searchKey || 'search'}=${encodeURIComponent(searchValue)}` : '';
  const companyQ = companyId ? `&company_id=${encodeURIComponent(companyId)}` : '';
  const url = userId
    ? `/assign-vehicle-access/list?userId=${encodeURIComponent(userId)}&limit=${limit}&offset=${offset}${searchQ}${companyQ}`
    : null;

  const { data, error } = useSWR(url, async (u) => {
    const resp = await fleetApi.get(u + getTzParam());
    const payload = resp?.data ?? resp;
    let rows = Array.isArray(payload) ? payload : (payload?.data ?? payload?.result ?? payload?.rows ?? []);
    rows = rows.filter((r) => r.selected === true);
    return { rows, total: payload?.totalRecord ?? rows.length };
  });

  return {
    data: data?.rows ?? [],
    totalRecords: data?.total ?? 0,
    isLoading: !error && !data,
    isError: error
  };
};

const useFilteredCompanyList = (page, pageSize, searchKey, searchValue) => {
  const accessReq = useUserCompanyAccessHook(0, 1000, '', '');
  const allowedIds = useMemo(() => accessReq.data.map((c) => String(c.company_access || c.key)), [accessReq.data]);

  // Fetch all to allow proper client-side filtering and pagination
  const masterReq = useCompanyDataList(0, 10000, searchKey, searchValue);

  const filteredData = useMemo(() => {
    if (accessReq.isLoading || masterReq.isLoading) return [];
    if (allowedIds.length === 0) return masterReq.data; // fallback if no access check done yet
    return masterReq.data.filter((c) => allowedIds.includes(String(c.id)));
  }, [masterReq.data, allowedIds, accessReq.isLoading, masterReq.isLoading]);

  const paginatedData = useMemo(() => {
    return filteredData.slice(page * pageSize, (page + 1) * pageSize);
  }, [filteredData, page, pageSize]);

  return {
    data: paginatedData,
    totalRecord: filteredData.length,
    isLoading: masterReq.isLoading || accessReq.isLoading,
    isError: masterReq.isError || accessReq.isError
  };
};

const useFilteredVehicleList = (page, pageSize, searchKey, searchValue, companyIdStr) => {
  const accessReq = useUserVehicleAccessHook(0, 1000, '', '', '');
  const allowedIds = useMemo(() => accessReq.data.map((v) => String(v.vehicle_access || v.key)), [accessReq.data]);

  const companyIdArr = useMemo(() => {
    return companyIdStr
      ? companyIdStr
          .split(',')
          .filter(Boolean)
          .map((c) => ({ value: c }))
      : [];
  }, [companyIdStr]);

  // Fetch all to allow proper client-side filtering and pagination
  const masterReq = useMasterVehicleList(0, 10000, companyIdArr, searchKey, searchValue);

  const filteredData = useMemo(() => {
    if (accessReq.isLoading || masterReq.isLoading) return [];
    if (allowedIds.length === 0) return masterReq.data; // fallback
    return masterReq.data.filter((v) => allowedIds.includes(String(v.id)));
  }, [masterReq.data, allowedIds, accessReq.isLoading, masterReq.isLoading]);

  const paginatedData = useMemo(() => {
    return filteredData.slice(page * pageSize, (page + 1) * pageSize);
  }, [filteredData, page, pageSize]);

  return {
    data: paginatedData,
    totalRecord: filteredData.length,
    isLoading: masterReq.isLoading || accessReq.isLoading,
    isError: masterReq.isError || accessReq.isError
  };
};

// ==============================|| HELPER FUNCTIONS ||============================== //

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d
    .toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
    .replace(/\./g, ':');
};

const getNotifTypeColor = (type) => {
  const t = String(type).toLowerCase();
  if (t.includes('over speed') || t.includes('panic') || t.includes('danger') || t.includes('drop power volt') || t.includes('low battery'))
    return 'error';
  if (t.includes('harsh')) return 'error';
  if (t.includes('enter')) return 'success';
  if (t.includes('exit')) return 'primary';
  if (t.includes('door') || t.includes('bad gsm') || t.includes('bad position') || t.includes('under')) return 'warning';
  return 'default';
};

const formatNotifTypeName = (type, t) => {
  const str = String(type || 'Alert');
  const lowerT = str.toLowerCase();
  if (str.toLowerCase().includes('breaking')) return t ? t('notification.harshBraking', 'Harsh Braking') : 'Harsh Braking';
  if (
    lowerT.includes('enter pool') ||
    lowerT.includes('enter pit') ||
    lowerT.includes('enter customer') ||
    lowerT.includes('enter jetty') ||
    lowerT.includes('enter geofence')
  )
    return t ? t('notification.enterLocation', 'Enter Location') : 'Enter Location';
  if (
    lowerT.includes('exit pool') ||
    lowerT.includes('exit pit') ||
    lowerT.includes('exit customer') ||
    lowerT.includes('exit jetty') ||
    lowerT.includes('exit geofence') ||
    lowerT.includes('exit polygon')
  )
    return t ? t('notification.exitLocation', 'Exit Location') : 'Exit Location';
  return str;
};

const formatDescription = (desc, t) => {
  if (!desc) return '-';
  let str = String(desc);

  str = str.replace(/Pool/g, 'PIT').replace(/Customer/g, 'Jetty');

  const enterLocStr = t ? t('notification.enterLocation', 'Enter Location') : 'Enter Location';
  const exitLocStr = t ? t('notification.exitLocation', 'Exit Location') : 'Exit Location';
  const harshBrakingStr = t ? t('notification.harshBraking', 'Harsh Braking') : 'Harsh Braking';

  if (str.includes('|')) {
    let parts = str.split('|');
    if (parts[0].toLowerCase().includes('enter')) {
      parts[0] = parts[0].replace(/Enter\s+[^|]+/i, `${enterLocStr} `);
    } else if (parts[0].toLowerCase().includes('exit')) {
      parts[0] = parts[0].replace(/Exit\s+[^|]+/i, `${exitLocStr} `);
    }
    return parts.join('|');
  }

  if (str.toLowerCase().includes('enter')) {
    return str.replace(/Enter\s+[a-zA-Z]+/i, enterLocStr);
  } else if (str.toLowerCase().includes('exit')) {
    return str.replace(/Exit\s+[a-zA-Z]+/i, exitLocStr);
  }
  if (str.toLowerCase().includes('breaking')) return harshBrakingStr;

  return str;
};

// ==============================|| LOCATION CELL COMPONENT ||============================== //
const LocationCell = ({ row }) => {
  const [locString, setLocString] = useState(row.location || '');

  useEffect(() => {
    let isMounted = true;
    if ((!row.location || !row.location.includes('~')) && row.lat && row.lon) {
      const url = `${API_BASES.ADDRESS}/reverse.php?lat=${row.lat}&lon=${row.lon}&zoom=18&format=json&addressdetails=1&limit=1`;
      axios
        .get(url)
        .then((res) => {
          if (isMounted) {
            const name = res.data?.display_name || res.data?.data?.display_name || 'Location unknown';
            setLocString(`~ ${name}`);
          }
        })
        .catch(() => {});
    }
    return () => {
      isMounted = false;
    };
  }, [row.location, row.lat, row.lon]);

  const displayLocString = row.spatial_name && locString?.includes('~') ? locString.split('~').pop().trim() : locString;

  const fullText =
    row.spatial_name && displayLocString ? `${row.spatial_name} - ${displayLocString}` : displayLocString || row.spatial_name || '';

  return (
    <Tooltip title={fullText}>
      <Typography
        component={row.lat && row.lon ? 'a' : 'p'}
        href={row.lat && row.lon ? `http://maps.google.com/maps?z=18&q=${row.lat},${row.lon}` : undefined}
        target={row.lat && row.lon ? '_blank' : undefined}
        rel={row.lat && row.lon ? 'noopener noreferrer' : undefined}
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          ...(row.lat &&
            row.lon && {
              textDecoration: 'none',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline'
              }
            })
        }}
      >
        {row.spatial_name ? <b>{row.spatial_name}</b> : null}
        {row.spatial_name && displayLocString ? ' - ' : ''}
        {displayLocString}
      </Typography>
    </Tooltip>
  );
};

// ==============================|| MAIN COMPONENT ||============================== //

export default function NotificationDialog({ open, onClose, initialTab = 0 }) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      setPage(0);
    }
  }, [open, initialTab]);

  // User defaults
  const defaultCompanyId = localStorage.getItem('companyId') || '';

  const { companyOptions = [] } = useCompanyList();

  const genCompanyPickerParams = usePickerPagination({
    fetchHook: useFilteredCompanyList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [generalData, setGeneralData] = useState([]);
  const [genTotalRecords, setGenTotalRecords] = useState(0);
  const [genLoading, setGenLoading] = useState(false);
  const [genSelectedCompanies, setGenSelectedCompanies] = useState(() => {
    const defaultCompany = companyOptions.find((c) => String(c.value) === String(defaultCompanyId));
    return defaultCompany ? [defaultCompany] : [];
  });

  useEffect(() => {
    if (companyOptions.length > 0 && genSelectedCompanies.length === 0 && defaultCompanyId) {
      const match = companyOptions.find((c) => String(c.value) === String(defaultCompanyId));
      if (match) setGenSelectedCompanies([match]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyOptions, defaultCompanyId]);

  const [genSelectedVehicles, setGenSelectedVehicles] = useState([]);
  const [genSearchText, setGenSearchText] = useState('');
  const [genTriggerSearch, setGenTriggerSearch] = useState(0);

  const validGenSelectedCompanies = useMemo(() => {
    return genSelectedCompanies.filter((c) => c.value !== 'all');
  }, [genSelectedCompanies]);

  const genVehiclePickerParams = usePickerPagination({
    fetchHook: useFilteredVehicleList,
    buildArgs: (state) => [
      state.page,
      state.pageSize,
      state.searchKey,
      state.searchValue,
      validGenSelectedCompanies.map((c) => c.value || c.id || c.company_access || c.key).join(',')
    ],
    searchKey: 'license_plate'
  });

  const [genPeriod, setGenPeriod] = useState('today');
  const [genStartDate, setGenStartDate] = useState(dayjs().startOf('day'));
  const [genEndDate, setGenEndDate] = useState(dayjs().endOf('day'));
  const [genNotifTypeFilter, setGenNotifTypeFilter] = useState('all');

  const handlePeriodChange = (e) => {
    const val = e.target.value;
    setGenPeriod(val);
    if (val === 'today') {
      setGenStartDate(dayjs().startOf('day'));
      setGenEndDate(dayjs().endOf('day'));
    } else if (val === 'last_week') {
      setGenStartDate(dayjs().subtract(7, 'day').startOf('day'));
      setGenEndDate(dayjs().endOf('day'));
    }
  };

  const [docsData, setDocsData] = useState([]);
  const [docsTotalRecords, setDocsTotalRecords] = useState(0);
  const [docsPage, setDocsPage] = useState(0);
  const [docsRowsPerPage, setDocsRowsPerPage] = useState(10);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsCompanyId, setDocsCompanyId] = useState(defaultCompanyId ? [defaultCompanyId] : []);
  const [docsNotifType, setDocsNotifType] = useState('all');
  const [docsSearchName, setDocsSearchName] = useState('');
  const [docsSearchNotifName, setDocsSearchNotifName] = useState('');
  const [docsTriggerSearch, setDocsTriggerSearch] = useState(0);

  const docsSelectedCompanies = useMemo(() => {
    return Array.isArray(docsCompanyId) ? docsCompanyId.map((id) => ({ value: id })) : [];
  }, [docsCompanyId]);

  const docsCompanyPickerParams = usePickerPagination({
    fetchHook: useFilteredCompanyList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const docsCompanyIdString = Array.isArray(docsCompanyId) ? docsCompanyId.join(',') : '';

  const docsDriverPickerParams = usePickerPagination({
    fetchHook: useDriverList,
    buildArgs: (state) => [state.page, state.pageSize, docsSelectedCompanies, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const docsDocsVehiclePickerParams = usePickerPagination({
    fetchHook: useFilteredVehicleList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, docsCompanyIdString],
    searchKey: 'license_plate',
    formatOption: (item) => ({ value: item.id || item.value, label: item.license_plate || item.name || String(item.id) })
  });

  const docsDriverLicensePickerParams = usePickerPagination({
    fetchHook: useDriverLicenseList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, docsCompanyIdString],
    searchKey: 'license_name',
    formatOption: (item) => ({ value: item.id || item.value, label: item.license_name || item.name || String(item.id) })
  });

  const docsServiceTypePickerParams = usePickerPagination({
    fetchHook: useServiceTypeList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, docsCompanyIdString],
    searchKey: 'name',
    formatOption: (item) => ({ value: item.id || item.value, label: item.name || String(item.id) })
  });

  const docsVehicleLicensePickerParams = usePickerPagination({
    fetchHook: useVehicleLicenseList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, docsCompanyIdString],
    searchKey: 'license_name',
    formatOption: (item) => ({ value: item.id || item.value, label: item.license_name || item.name || String(item.id) })
  });

  const activeNamePickerParams = useMemo(() => {
    if (docsNotifType === 'driver-reminder') return docsDriverPickerParams;
    if (docsNotifType === 'service-reminder' || docsNotifType === 'vehicle-reminder') return docsDocsVehiclePickerParams;
    return null;
  }, [docsNotifType, docsDriverPickerParams, docsDocsVehiclePickerParams]);

  const activeNotifNamePickerParams = useMemo(() => {
    if (docsNotifType === 'driver-reminder') return docsDriverLicensePickerParams;
    if (docsNotifType === 'service-reminder') return docsServiceTypePickerParams;
    if (docsNotifType === 'vehicle-reminder') return docsVehicleLicensePickerParams;
    return null;
  }, [docsNotifType, docsDriverLicensePickerParams, docsServiceTypePickerParams, docsVehicleLicensePickerParams]);

  const getNameLabel = () => {
    if (docsNotifType === 'driver-reminder') return 'Driver';
    if (docsNotifType === 'service-reminder' || docsNotifType === 'vehicle-reminder') return 'Vehicle';
    return 'Name';
  };

  const getNotifNameLabel = () => {
    if (docsNotifType === 'driver-reminder') return 'Driver License';
    if (docsNotifType === 'vehicle-reminder') return 'Vehicle License';
    if (docsNotifType === 'service-reminder') return 'Service Type';
    return 'Notif Name';
  };

  useEffect(() => {
    if (!open) return;
    const fetchGeneral = async () => {
      setGenLoading(true);
      try {
        const columns = [];
        const validCompanies = genSelectedCompanies.filter((c) => c.value !== 'all');
        if (validCompanies.length > 0) {
          const values = validCompanies.map((c) => String(c.value));
          if (values.length === 1) {
            columns.push({ logic_operator: '=', name: 'company_id', operator: 'and', value: values[0] });
          } else {
            columns.push({ logic_operator: 'in', name: 'company_id', operator: 'and', value: values.join(',') });
          }
        }

        const validVehicles = genSelectedVehicles.filter((v) => v.value !== 'all' && v.id !== 'all');
        if (validVehicles.length > 0) {
          const values = validVehicles.map((v) => String(v.id || v.value));
          if (values.length === 1) {
            columns.push({ logic_operator: '=', name: 'vehicle_id', operator: 'and', value: values[0] });
          } else {
            columns.push({ logic_operator: 'in', name: 'vehicle_id', operator: 'and', value: values.join(',') });
          }
        }

        const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
        if (genStartDate && genEndDate) {
          const startTimeVal = genStartDate.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
          const finishTimeVal = genEndDate.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
          columns.push(
            {
              logic_operator: 'range',
              name: 'start_time',
              operator: 'or',
              value: startTimeVal,
              value1: finishTimeVal
            },
            {
              logic_operator: 'range',
              name: 'finish_time',
              operator: 'or',
              value: startTimeVal,
              value1: finishTimeVal
            }
          );
        }

        if (genNotifTypeFilter !== 'all') {
          const filterVal = genNotifTypeFilter;
          if (filterVal === 'Enter Location') {
            columns.push({ logic_operator: 'in', name: 'notif_type', operator: 'and', value: 'Enter Pool,Enter Geofence,Enter Customer' });
          } else if (filterVal === 'Exit Polygon') {
            columns.push({ logic_operator: '=', name: 'notif_type', operator: 'and', value: 'Exit Polygon' });
          } else {
            columns.push({ logic_operator: '=', name: 'notif_type', operator: 'and', value: filterVal });
          }
        }
        const payload = {
          paging: { length: rowsPerPage, start: page * rowsPerPage },
          columns,
          orders: { ascending: false, columns: ['start_time', 'finish_time'] }
        };

        const res = await fetchHistoryGpsNotifierSearch(payload, tz);
        const fetchedData = res?.data?.data || res?.data || [];
        const total = res?.data?.totalRecord ?? res?.totalRecord ?? 0;

        setGeneralData(Array.isArray(fetchedData) ? fetchedData : []);
        setGenTotalRecords(total);
      } catch (err) {
        console.error(err);
        setGeneralData([]);
        setGenTotalRecords(0);
      } finally {
        setGenLoading(false);
      }
    };
    fetchGeneral();

    let interval;
    if (genPeriod === 'today') {
      interval = setInterval(() => {
        fetchGeneral();
      }, 60000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, rowsPerPage, genTriggerSearch, genPeriod, genStartDate, genEndDate]);

  // Fetch Docs Data
  useEffect(() => {
    if (!open) return;
    const fetchDocs = async () => {
      setDocsLoading(true);
      try {
        const res = await fetchAlertNotificationDocsSearch({
          start: docsPage * docsRowsPerPage,
          length: docsRowsPerPage,
          companyId: Array.isArray(docsCompanyId) ? docsCompanyId.join(',') : String(docsCompanyId),
          notifType: docsNotifType,
          name: docsSearchName !== undefined && docsSearchName !== null ? String(docsSearchName) : docsSearchName,
          notifName: docsSearchNotifName !== undefined && docsSearchNotifName !== null ? String(docsSearchNotifName) : docsSearchNotifName
        });
        const fetchedData = res?.data?.data || res?.data || [];
        const total = res?.data?.totalRecord ?? res?.totalRecord ?? 0;
        setDocsData(Array.isArray(fetchedData) ? fetchedData : []);
        setDocsTotalRecords(total);
      } catch (err) {
        console.error(err);
        setDocsData([]);
        setDocsTotalRecords(0);
      } finally {
        setDocsLoading(false);
      }
    };
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, docsTriggerSearch, docsPage, docsRowsPerPage]);

  const handleDocsChangePage = (event, newPage) => {
    setDocsPage(newPage);
  };
  const handleDocsChangeRowsPerPage = (event) => {
    setDocsRowsPerPage(parseInt(event.target.value, 10));
    setDocsPage(0);
  };

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const openExportMenu = Boolean(exportAnchorEl);

  const [docsExportAnchorEl, setDocsExportAnchorEl] = useState(null);
  const openDocsExportMenu = Boolean(docsExportAnchorEl);

  const handleDocsExport = async (type) => {
    setDocsExportAnchorEl(null);
    setDocsLoading(true);
    try {
      const total = docsTotalRecords > 0 ? docsTotalRecords : 1000;
      let allData = [];
      const limit = 1000;

      for (let start = 0; start < total; start += limit) {
        const res = await fetchAlertNotificationDocsSearch({
          start,
          length: limit,
          companyId: Array.isArray(docsCompanyId) ? docsCompanyId.join(',') : String(docsCompanyId),
          notifType: docsNotifType,
          name: docsSearchName,
          notifName: docsSearchNotifName
        });
        const fetchedData = res?.data?.data || res?.data || [];
        if (Array.isArray(fetchedData)) {
          allData = allData.concat(fetchedData);
        }
        if (!fetchedData.length) break;
      }

      const formattedData = allData.map((row) => ({
        Name: row.name || '-',
        'License No / Phone': row.licenseNo || row.phone || '-',
        Type: row.notifType || '-',
        'Document Name': row.notifName || '-',
        'Exp Date / Next Odo': row.expiredDate || row.targetOdo || '-',
        'Day Left / Odo Left': row.dayLeft !== undefined ? `${row.dayLeft} Days` : row.odoLeft !== undefined ? `${row.odoLeft} km` : '-',
        Status: row.statusNotif || '-'
      }));

      if (type === 'csv') {
        const csv = Papa.unparse(formattedData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `Document_Alerts_${dayjs().format('YYYYMMDD')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Document Alerts');
        if (formattedData.length > 0) {
          worksheet.columns = Object.keys(formattedData[0]).map((key) => ({ header: key, key, width: 25 }));
          formattedData.forEach((row) => worksheet.addRow(row));
        }
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `Document_Alerts_${dayjs().format('YYYYMMDD')}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDocsLoading(false);
    }
  };

  const handleExport = async (type) => {
    setExportAnchorEl(null);
    setGenLoading(true);
    try {
      const total = genTotalRecords > 0 ? genTotalRecords : 1000;
      let allData = [];
      const limit = 1000;

      const columns = [];
      const validCompanies = genSelectedCompanies.filter((c) => c.value !== 'all');
      if (validCompanies.length > 0) {
        const values = validCompanies.map((c) => String(c.value));
        if (values.length === 1) columns.push({ logic_operator: '=', name: 'company_id', operator: 'and', value: values[0] });
        else columns.push({ logic_operator: 'in', name: 'company_id', operator: 'and', value: values.join(',') });
      }

      const validVehicles = genSelectedVehicles.filter((v) => v.value !== 'all' && v.id !== 'all');
      if (validVehicles.length > 0) {
        const values = validVehicles.map((v) => String(v.id || v.value));
        if (values.length === 1) columns.push({ logic_operator: '=', name: 'vehicle_id', operator: 'and', value: values[0] });
        else columns.push({ logic_operator: 'in', name: 'vehicle_id', operator: 'and', value: values.join(',') });
      }

      const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
      if (genStartDate && genEndDate) {
        columns.push({
          logic_operator: 'range',
          name: 'start_time',
          operator: 'and',
          value: genStartDate.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]'),
          value1: genEndDate.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]')
        });
      }

      if (genNotifTypeFilter !== 'all') {
        const filterVal = genNotifTypeFilter;
        if (filterVal === 'Enter Location')
          columns.push({ logic_operator: 'in', name: 'notif_type', operator: 'and', value: 'Enter Pool,Enter Geofence,Enter Customer' });
        else if (filterVal === 'Exit Polygon')
          columns.push({ logic_operator: '=', name: 'notif_type', operator: 'and', value: 'Exit Polygon' });
        else columns.push({ logic_operator: '=', name: 'notif_type', operator: 'and', value: filterVal });
      }

      let filters = [];
      if (genSearchText) {
        filters = [
          { name: 'license_plate', value: genSearchText },
          { name: 'description', value: genSearchText },
          { name: 'spatial_name', value: genSearchText }
        ];
      }

      for (let start = 0; start < total; start += limit) {
        const payload = {
          paging: { length: limit, start },
          columns,
          filters,
          orders: { ascending: false, columns: ['start_time', 'finish_time'] }
        };
        const res = await fetchHistoryGpsNotifierSearch(payload, tz);
        const fetchedData = res?.data?.data || res?.data || [];
        if (Array.isArray(fetchedData)) {
          allData = allData.concat(fetchedData);
        }
        if (!fetchedData.length) break;
      }

      const formattedData = allData.map((row) => ({
        'Start Time': formatDate(row.start_time),
        'Finish Time': row.finish_time ? formatDate(row.finish_time) : '-',
        Vehicle: row.license_plate,
        Type: formatNotifTypeName(row.notif_type, t),
        Description: formatDescription(row.description, t),
        'Speed (km/h)': row.speed,
        Duration: row.duration || '-',
        'Spatial Name': row.spatial_name || '',
        Location: row.location || ''
      }));

      if (type === 'csv') {
        const csv = Papa.unparse(formattedData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `Notifications_${dayjs().format('YYYYMMDD')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Notifications');
        if (formattedData.length > 0) {
          worksheet.columns = Object.keys(formattedData[0]).map((key) => ({ header: key, key, width: 25 }));
          formattedData.forEach((row) => worksheet.addRow(row));
        }
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `Notifications_${dayjs().format('YYYYMMDD')}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setGenLoading(false);
    }
  };

  const currentGeneralData = useMemo(() => {
    if (!genSearchText) return generalData;
    const lowerSearch = genSearchText.toLowerCase();
    return generalData.filter((row) => {
      const vehicleMatch = (row.license_plate || '').toLowerCase().includes(lowerSearch);
      const locMatch =
        (row.location || '').toLowerCase().includes(lowerSearch) || (row.spatial_name || '').toLowerCase().includes(lowerSearch);
      const descMatch =
        (row.description || '').toLowerCase().includes(lowerSearch) || (row.notif_type || '').toLowerCase().includes(lowerSearch);
      return vehicleMatch || locMatch || descMatch;
    });
  }, [generalData, genSearchText]);

  const currentDocsData = docsData;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="2xl"
      PaperProps={{ sx: { minHeight: '85vh', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'sticky',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          bgcolor: '#f8fafc'
        }}
      >
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 48 }}>
          <Tab
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <WarningAmberRoundedIcon fontSize="small" /> {t('notification.generalAlerts', 'General Alerts')} ({genTotalRecords})
              </span>
            }
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          />
          <Tab
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <DescriptionOutlinedIcon fontSize="small" /> {t('notification.documentAlerts', 'Document Alerts')} ({docsData.length})
              </span>
            }
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          />
        </Tabs>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: 'text.secondary', bgcolor: '#f1f5f9' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ p: 0, bgcolor: '#f1f5f9', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* --- TAB 0: GENERAL ALERTS --- */}
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Filters */}
            <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item size={{ xs: 12, sm: 3 }}>
                  <PickerField
                    label="Company"
                    multiSelect={true}
                    value={genSelectedCompanies.map((c) => String(c.value || c.id || c.company_access || c.key))}
                    {...genCompanyPickerParams}
                    options={genCompanyPickerParams.options.map((c) => ({
                      value: String(c.id || c.company_access || c.key || c.value),
                      label: c.name || c.company_name || c.label || 'Unknown',
                      ...c
                    }))}
                    listOptions={(genCompanyPickerParams.listOptions || []).map((c) => ({
                      value: String(c.id || c.company_access || c.key || c.value),
                      label: c.name || c.company_name || c.label || 'Unknown',
                      ...c
                    }))}
                    onChange={(opts) => {
                      // opts is array of values
                      const selected = opts.map((val) => {
                        return (
                          genCompanyPickerParams.options.find(
                            (o) => String(o.company_access || o.key || o.value || o.id) === String(val)
                          ) || { value: val }
                        );
                      });
                      setGenSelectedCompanies(selected);
                      setGenSelectedVehicles([]);
                    }}
                    dialogTitle="Select Company"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 3 }}>
                  <PickerField
                    label="Vehicle"
                    multiSelect={true}
                    value={genSelectedVehicles.map((v) => String(v.value || v.id || v.vehicle_access || v.key))}
                    {...genVehiclePickerParams}
                    options={genVehiclePickerParams.options.map((v) => ({
                      value: String(v.id || v.vehicle_access || v.key || v.value),
                      label: v.license_plate || v.vehicle_license_plate || v.name || v.label || 'Unknown',
                      ...v
                    }))}
                    listOptions={(genVehiclePickerParams.listOptions || []).map((v) => ({
                      value: String(v.id || v.vehicle_access || v.key || v.value),
                      label: v.license_plate || v.vehicle_license_plate || v.name || v.label || 'Unknown',
                      ...v
                    }))}
                    onChange={(opts) => {
                      const selected = opts.map((val) => {
                        return (
                          genVehiclePickerParams.options.find(
                            (o) => String(o.vehicle_access || o.key || o.value || o.id) === String(val)
                          ) || { value: val }
                        );
                      });
                      setGenSelectedVehicles(selected);
                    }}
                    dialogTitle="Select Vehicle"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 2 }}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>{t('notification.period', 'Periode')}</InputLabel>
                    <Select value={genPeriod} onChange={handlePeriodChange} label={t('notification.period', 'Periode')}>
                      <MenuItem value="today">{t('notification.last24Hours', 'Last 24 jam')}</MenuItem>
                      <MenuItem value="last_week">{t('notification.lastWeek', 'Last Week')}</MenuItem>
                      <MenuItem value="custom">{t('notification.custom', 'Custom')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {genPeriod === 'custom' && (
                  <>
                    <Grid item size={{ xs: 12, sm: 2 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          timeSteps={{ minutes: 1 }}
                          label={t('notification.startDate', 'Start Date')}
                          format="DD/MM/YYYY HH:mm"
                          ampm={false}
                          value={genStartDate}
                          onChange={(newValue) => setGenStartDate(newValue)}
                          maxDate={genEndDate || dayjs()}
                          minDate={dayjs().subtract(7, 'day').startOf('day')}
                          slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 2 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          timeSteps={{ minutes: 1 }}
                          label={t('notification.endDate', 'End Date')}
                          format="DD/MM/YYYY HH:mm"
                          ampm={false}
                          value={genEndDate}
                          onChange={(newValue) => setGenEndDate(newValue)}
                          minDate={genStartDate || dayjs().subtract(7, 'day').startOf('day')}
                          maxDate={
                            genStartDate ? (genStartDate.add(7, 'day').isBefore(dayjs()) ? genStartDate.add(7, 'day') : dayjs()) : dayjs()
                          }
                          slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </>
                )}
                <Grid item size={{ xs: 12, sm: 2 }}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>{t('notification.alertType', 'Alert Type')}</InputLabel>
                    <Select
                      value={genNotifTypeFilter}
                      onChange={(e) => setGenNotifTypeFilter(e.target.value)}
                      label={t('notification.alertType', 'Alert Type')}
                    >
                      <MenuItem value="all">{t('notification.allAlerts', 'All Alerts')}</MenuItem>
                      <MenuItem value="Harsh Acceleration">{t('notification.harshAcceleration', 'Harsh Acceleration')}</MenuItem>
                      <MenuItem value="Harsh Breaking">{t('notification.harshBraking', 'Harsh Braking')}</MenuItem>
                      <MenuItem value="Harsh Cornering">{t('notification.harshCornering', 'Harsh Cornering')}</MenuItem>
                      <MenuItem value="Over Speed">{t('notification.overSpeed', 'Over Speed')}</MenuItem>
                      <MenuItem value="Under Speed">{t('notification.underSpeed', 'Under Speed')}</MenuItem>
                      <MenuItem value="Enter Location">{t('notification.enterLocation', 'Enter Location')}</MenuItem>
                      <MenuItem value="Exit Polygon">{t('notification.exitLocation', 'Exit Location')}</MenuItem>
                      <MenuItem value="Bad Position">{t('notification.badPosition', 'Bad Position')}</MenuItem>
                      <MenuItem value="Low Battery">{t('notification.lowBattery', 'Low Battery')}</MenuItem>
                      <MenuItem value="Bad GSM Signal">{t('notification.badGsmSignal', 'Bad GSM Signal')}</MenuItem>
                      <MenuItem value="Drop Power Volt">{t('notification.dropPowerVolt', 'Drop Power Volt')}</MenuItem>
                      <MenuItem value="Panic Button">{t('notification.panicButton', 'Panic Button')}</MenuItem>
                      <MenuItem value="Door Right Open">{t('notification.doorRightOpen', 'Door Right Open')}</MenuItem>
                      <MenuItem value="Door Left Open">{t('notification.doorLeftOpen', 'Door Left Open')}</MenuItem>
                      <MenuItem value="Panic Button">{t('notification.doorBehindOpen', 'Door Behind Open')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    size="medium"
                    label={t('notification.search', 'Search...')}
                    value={genSearchText}
                    onChange={(e) => {
                      setGenSearchText(e.target.value);
                      setPage(0);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                  />
                </Grid>

                <Grid item size={{ xs: 12, sm: 3 }} display="flex" justifyContent="flex-start">
                  <Button
                    variant="contained"
                    startIcon={genLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    onClick={() => {
                      setPage(0);
                      setGenTriggerSearch((prev) => prev + 1);
                    }}
                    disabled={genLoading}
                    sx={{ mr: 1 }}
                  >
                    {genLoading ? t('notification.searching', 'Searching...') : t('notification.searchBtn', 'Search')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    onClick={(e) => setExportAnchorEl(e.currentTarget)}
                    disabled={genLoading || genTotalRecords === 0}
                  >
                    {t('notification.export', 'Export')}
                  </Button>

                  <Menu anchorEl={exportAnchorEl} open={openExportMenu} onClose={() => setExportAnchorEl(null)}>
                    <MenuItem onClick={() => handleExport('csv')}>{t('notification.exportToCsv', 'Export to CSV')}</MenuItem>
                    <MenuItem onClick={() => handleExport('excel')}>{t('notification.exportToExcel', 'Export to Excel')}</MenuItem>
                  </Menu>
                </Grid>
                {genPeriod === 'custom' && (
                  <Grid item size={{ xs: 12, sm: 4 }} display="flex" justifyContent="flex-end">
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'info.main',
                        bgcolor: 'info.lighter',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'info.light',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        lineHeight: 1.2
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      {t('notification.maxRangeData', 'Maksimal rentang data: 7 hari terakhir')}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            {genLoading ? (
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ flexGrow: 1, minHeight: 0, borderRadius: 0, overflow: 'auto' }}>
                <Table size="medium" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', width: '15%' }}>
                        {t('notification.startEnd', 'Start - End')}
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', width: '12%' }}>
                        {t('notification.vehicle', 'Vehicle')}
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', width: '15%' }}>{t('notification.type', 'Type')}</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', width: '25%' }}>
                        {t('notification.description', 'Description')}
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', width: '13%' }}>
                        {t('notification.condition', 'Condition')}
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', width: '20%' }}>
                        {t('notification.location', 'Location')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentGeneralData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                          {t('notification.noNotificationHistory', 'Tidak ada riwayat notifikasi.')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentGeneralData.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" fontWeight="500">
                              {formatDate(row.start_time)}
                            </Typography>
                            {row.finish_time && row.finish_time !== row.start_time && (
                              <Typography variant="body2" fontWeight="500">
                                - {formatDate(row.finish_time)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="bold">{row.license_plate}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={formatNotifTypeName(row.notif_type, t)}
                              color={getNotifTypeColor(row.notif_type)}
                              size="small"
                              sx={{ fontWeight: 'bold', borderRadius: 1 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography>{formatDescription(row.description, t)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <SpeedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" fontWeight="bold">
                                  {row.speed} km/h
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TimerOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {row.duration || '-'}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <LocationCell row={row} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={genTotalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#fff', flexShrink: 0 }}
            />
          </Box>
        )}

        {/* --- TAB 1: DOCUMENT ALERTS --- */}
        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Filters */}
            <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item size={{ xs: 12, sm: 3 }}>
                  <PickerField
                    label="Company"
                    multiSelect={true}
                    value={Array.isArray(docsCompanyId) ? docsCompanyId : []}
                    {...docsCompanyPickerParams}
                    options={docsCompanyPickerParams.options.map((c) => ({
                      value: String(c.id || c.company_access || c.key || c.value),
                      label: c.name || c.company_name || c.label || 'Unknown',
                      ...c
                    }))}
                    listOptions={(docsCompanyPickerParams.listOptions || []).map((c) => ({
                      value: String(c.id || c.company_access || c.key || c.value),
                      label: c.name || c.company_name || c.label || 'Unknown',
                      ...c
                    }))}
                    onChange={(opts) => setDocsCompanyId(opts)}
                    dialogTitle="Select Company"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>{t('notification.documentType', 'Document Type')}</InputLabel>
                    <Select
                      value={docsNotifType}
                      onChange={(e) => {
                        setDocsNotifType(e.target.value);
                        setDocsSearchName('');
                        setDocsSearchNotifName('');
                      }}
                      label={t('notification.documentType', 'Document Type')}
                    >
                      <MenuItem value="all">{t('notification.allDocuments', 'All Documents')}</MenuItem>
                      <MenuItem value="driver-reminder">{t('notification.driverReminder', 'Driver Reminder')}</MenuItem>
                      <MenuItem value="service-reminder">{t('notification.serviceReminder', 'Service Reminder')}</MenuItem>
                      <MenuItem value="vehicle-reminder">{t('notification.vehicleReminder', 'Vehicle Reminder')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item size={{ xs: 12, sm: 2 }}>
                  {activeNamePickerParams ? (
                    <PickerField
                      label={getNameLabel()}
                      multiSelect={false}
                      value={docsSearchName}
                      {...activeNamePickerParams}
                      options={activeNamePickerParams.options.map((o) => ({
                        ...o,
                        value: String(
                          o.name || o.driver_name || o.license_plate || o.label || o.id || o.vehicle_id || o.driver_id || o.value || ''
                        ),
                        label: o.name || o.driver_name || o.license_plate || o.label || 'Unknown'
                      }))}
                      listOptions={(activeNamePickerParams.listOptions || []).map((o) => ({
                        ...o,
                        value: String(
                          o.name || o.driver_name || o.license_plate || o.label || o.id || o.vehicle_id || o.driver_id || o.value || ''
                        ),
                        label: o.name || o.driver_name || o.license_plate || o.label || 'Unknown'
                      }))}
                      onChange={(val) => setDocsSearchName(val || '')}
                      dialogTitle={'Select ' + getNameLabel()}
                      getOptionId={(o) => o?.value}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      size="medium"
                      label={getNameLabel()}
                      value={docsSearchName}
                      onChange={(e) => setDocsSearchName(e.target.value)}
                    />
                  )}
                </Grid>
                <Grid item size={{ xs: 12, sm: 2 }}>
                  {activeNotifNamePickerParams ? (
                    <PickerField
                      label={getNotifNameLabel()}
                      multiSelect={false}
                      value={docsSearchNotifName}
                      {...activeNotifNamePickerParams}
                      options={activeNotifNamePickerParams.options.map((o) => ({
                        ...o,
                        value: String(o.name || o.license_name || o.license_no || o.document_name || o.label || o.id || o.value || ''),
                        label: o.name || o.license_name || o.license_no || o.document_name || o.label || 'Unknown'
                      }))}
                      listOptions={(activeNotifNamePickerParams.listOptions || []).map((o) => ({
                        ...o,
                        value: String(o.name || o.license_name || o.license_no || o.document_name || o.label || o.id || o.value || ''),
                        label: o.name || o.license_name || o.license_no || o.document_name || o.label || 'Unknown'
                      }))}
                      onChange={(val) => setDocsSearchNotifName(val || '')}
                      dialogTitle={'Select ' + getNotifNameLabel()}
                      getOptionId={(o) => o?.value}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      size="medium"
                      label={getNotifNameLabel()}
                      value={docsSearchNotifName}
                      onChange={(e) => setDocsSearchNotifName(e.target.value)}
                    />
                  )}
                </Grid>
                <Grid item size={{ xs: 12, sm: 2 }} display="flex" justifyContent="flex-start" gap={1}>
                  <Button
                    variant="contained"
                    startIcon={docsLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    onClick={() => {
                      setDocsPage(0);
                      setDocsTriggerSearch((prev) => prev + 1);
                    }}
                    disabled={docsLoading}
                  >
                    {docsLoading ? t('notification.searching', 'Searching...') : t('notification.searchBtn', 'Search')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    onClick={(e) => setDocsExportAnchorEl(e.currentTarget)}
                    disabled={docsLoading || docsTotalRecords === 0}
                  >
                    {t('notification.export', 'Export')}
                  </Button>

                  <Menu anchorEl={docsExportAnchorEl} open={openDocsExportMenu} onClose={() => setDocsExportAnchorEl(null)}>
                    <MenuItem onClick={() => handleDocsExport('csv')}>{t('notification.exportToCsv', 'Export to CSV')}</MenuItem>
                    <MenuItem onClick={() => handleDocsExport('excel')}>{t('notification.exportToExcel', 'Export to Excel')}</MenuItem>
                  </Menu>
                </Grid>
              </Grid>
            </Box>

            {docsLoading ? (
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ flexGrow: 1, minHeight: 0, borderRadius: 0, overflow: 'auto' }}>
                <Table size="medium" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold' }}>{t('notification.name', 'Name')}</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold' }}>{t('notification.type', 'Type')}</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold' }}>
                        {t('notification.documentName', 'Document Name')}
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold' }}>
                        {t('notification.expDateNextOdo', 'Exp Date / Next Odo')}
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold' }}>
                        {t('notification.dayLeftOdoLeft', 'Day Left / Odo Left')}
                      </TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold' }}>{t('notification.status', 'Status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentDocsData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                          {t('notification.noDocumentNotification', 'Tidak ada dokumen notifikasi.')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentDocsData.map((row, idx) => {
                        const isDanger = String(row.statusNotif).toLowerCase() === 'danger';
                        return (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {row.name}
                              </Typography>
                              {row.licenseNo && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {row.licenseNo}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={row.notifType || '-'} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.notifName || '-'}</Typography>
                            </TableCell>
                            <TableCell>
                              {row.scheduleBy === 'Odometer' ? (
                                <Typography variant="body2">{row.nextServiceOdo || '-'}</Typography>
                              ) : (
                                <Typography variant="body2">{row.expDate ? formatDate(row.expDate) : '-'}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {row.scheduleBy === 'Odometer' ? (
                                <Typography variant="body2" color="error.main" fontWeight="bold">
                                  {row.restOdo} {t('notification.odoLeft', 'Odo Left')}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color={isDanger ? 'error.main' : 'warning.main'} fontWeight="bold">
                                  {row.dayLeft} {t('notification.daysLeft', 'Days Left')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row.statusNotif || '-'}
                                color={isDanger ? 'error' : 'warning'}
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={docsTotalRecords}
              rowsPerPage={docsRowsPerPage}
              page={docsPage}
              onPageChange={handleDocsChangePage}
              onRowsPerPageChange={handleDocsChangeRowsPerPage}
              sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#fff', flexShrink: 0 }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
